/**
 * WhatIfPanel — the fourth level of the Diversificazione area: "what happens if
 * I make *this* change I chose?".
 *
 * It never recomputes anything: the backend is the only source of the score, the
 * concentration, the duplication, the overlap and every delta. This component
 * configures the scenario (Add or Replace, candidate, weight) and reads the
 * Before / After the backend returns, keeping the uncertainty visible: no delta
 * of the score when the two sides are not comparable, unclassified mass always
 * separate, provenance of the candidate stated.
 *
 * No suggestion, no ranking, no "apply to portfolio": this is an analysis tool.
 */
import { useMemo, useState } from "react";
import axios from "axios";
import { AlertTriangle, ArrowRight, Loader2, Play } from "lucide-react";
import { SectionCard } from "@/components/system/SectionCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODES = [
  { id: "add", label: "Aggiungi ETF" },
  { id: "replace", label: "Sostituisci ETF" },
];

const CHANGE_LABEL = {
  introduced: { label: "nuova", variant: "success" },
  increased: { label: "aumenta", variant: "secondary" },
  decreased: { label: "diminuisce", variant: "secondary" },
  removed: { label: "scompare", variant: "danger" },
};

const num = (value, digits = 1) =>
  value === null || value === undefined ? "n/d" : Number(value).toFixed(digits);
const pct = (value, digits = 1) =>
  value === null || value === undefined ? "n/d" : `${num(value, digits)}%`;
const signed = (value, digits = 1) => {
  if (value === null || value === undefined) return "n/d";
  const fixed = num(Math.abs(value), digits);
  if (Number(value) === 0) return `0${digits ? "" : ""}`;
  return `${Number(value) > 0 ? "+" : "−"}${fixed}`;
};

export function WhatIfPanel({
  sessionId, dimension, subjects = [], knownEtfs = [], portfolioIsins = [],
}) {
  const [mode, setMode] = useState("add");
  const [candidate, setCandidate] = useState("");
  const [weight, setWeight] = useState(10);
  const [replaced, setReplaced] = useState(subjects[0]?.key || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [allExposures, setAllExposures] = useState(false);

  // Identity of a fund is its ISIN, never its ticker: two different funds can
  // share a display ticker. The backend stays the authority (409), this only
  // keeps the list honest.
  const inPortfolio = useMemo(
    () => new Set(portfolioIsins.map((isin) => String(isin).trim().toUpperCase())),
    [portfolioIsins],
  );
  const eligible = useMemo(
    () => knownEtfs.filter(
      (etf) => etf.isin && !inPortfolio.has(String(etf.isin).trim().toUpperCase()),
    ),
    [knownEtfs, inPortfolio],
  );
  const matching = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return eligible;
    return eligible.filter(
      (etf) => `${etf.name} ${etf.ticker || ""} ${etf.isin}`
        .toLowerCase().includes(needle),
    );
  }, [eligible, search]);
  const candidates = useMemo(() => matching.slice(0, 60), [matching]);

  const run = () => {
    if (!sessionId || !candidate) return;
    setLoading(true);
    setError(null);
    setAllExposures(false);
    axios
      .get(`${API}/diversification/whatif/${sessionId}`, {
        params: {
          mode, candidate_isin: candidate, dimension,
          weight_pct: mode === "add" ? Number(weight) || 10 : 10,
          ...(mode === "replace" ? { replace: replaced } : {}),
        },
      })
      .then(({ data: payload }) => setData(payload))
      .catch((err) => {
        setData(null);
        const detail = err?.response?.data?.detail;
        setError(
          detail === "candidate_already_in_portfolio"
            ? "Questo ETF è già nel portafoglio: scegline un altro (per cambiare i pesi usa la vista Allocazione)."
            : detail === "candidate_without_holdings"
              ? "Per questo ETF non ci sono holdings nel catalogo: non è possibile simularlo."
              : "Non è stato possibile calcolare la simulazione.",
        );
      })
      .finally(() => setLoading(false));
  };

  const score = data?.score;
  const scenario = data?.scenario;

  return (
    <SectionCard
      testId="whatif-panel"
      title="What-if: aggiungi o sostituisci un ETF"
      description="Simula una modifica che scegli tu e leggi come cambierebbe la struttura del portafoglio. Misura la diversificazione strutturale delle esposizioni: non è una stima di rendimento, di rischio di mercato o di qualità dell'investimento. La simulazione è read-only e non tocca il tuo portafoglio."
      density="compact"
    >
      <div className="space-y-5">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label htmlFor="whatif-mode" className="block text-label text-content-muted mb-1">
              Scenario
            </label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger id="whatif-mode" className="h-10" data-testid="whatif-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MODES.map((item) => (
                  <SelectItem key={item.id} value={item.id} data-testid={`whatif-mode-${item.id}`}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="lg:col-span-2">
            <label htmlFor="whatif-candidate" className="block text-label text-content-muted mb-1">
              ETF candidato
            </label>
            <Input
              id="whatif-candidate-filter"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filtra il catalogo per nome, ticker o ISIN"
              className="h-10 mb-2"
              data-testid="whatif-candidate-filter"
            />
            <Select value={candidate} onValueChange={setCandidate}>
              <SelectTrigger id="whatif-candidate" className="h-10" data-testid="whatif-candidate">
                <SelectValue placeholder="Scegli un ETF dal catalogo" />
              </SelectTrigger>
              <SelectContent>
                {candidates.map((etf) => (
                  <SelectItem key={etf.isin} value={etf.isin} data-testid={`whatif-candidate-${etf.isin}`}>
                    {etf.name || etf.isin}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-label text-content-muted mt-1" data-testid="whatif-candidate-count">
              {matching.length > candidates.length
                ? `${candidates.length} ETF su ${matching.length} corrispondenti: restringi il filtro per vedere gli altri.`
                : `${matching.length} ETF selezionabili (esclusi quelli già in portafoglio).`}
            </p>
          </div>

          {mode === "add" ? (
            <div>
              <label htmlFor="whatif-weight" className="block text-label text-content-muted mb-1">
                Peso del candidato (%)
              </label>
              <Input
                id="whatif-weight"
                type="number"
                min={1}
                max={99}
                step={1}
                value={weight}
                onChange={(event) => setWeight(event.target.value)}
                className="h-10"
                data-testid="whatif-weight"
              />
              <p className="text-label text-content-muted mt-1">
                Gli ETF già presenti mantengono le loro proporzioni nel restante{" "}
                {num(100 - (Number(weight) || 0), 0)}%.
              </p>
            </div>
          ) : (
            <div>
              <label htmlFor="whatif-replaced" className="block text-label text-content-muted mb-1">
                ETF da sostituire
              </label>
              <Select value={replaced} onValueChange={setReplaced}>
                <SelectTrigger id="whatif-replaced" className="h-10" data-testid="whatif-replaced">
                  <SelectValue placeholder="Scegli un ETF del portafoglio" />
                </SelectTrigger>
                <SelectContent>
                  {subjects.map((subject) => (
                    <SelectItem key={subject.key} value={subject.key} data-testid={`whatif-replaced-${subject.key}`}>
                      {subject.label || subject.key}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-label text-content-muted mt-1">
                Il candidato prende lo stesso peso; gli altri ETF restano invariati.
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={run}
            disabled={!candidate || loading || (mode === "replace" && !replaced)}
            className="h-10"
            data-testid="whatif-run"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
            ) : (
              <Play className="w-4 h-4 mr-2" aria-hidden="true" />
            )}
            Simula
          </Button>
          {error && (
            <p className="text-sm text-danger" data-testid="whatif-error">{error}</p>
          )}
        </div>

        {data && (
          <div className="space-y-5" data-testid="whatif-result">
            <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
              <div>
                <p className="text-label text-content-muted">Diversification Score</p>
                <p className="text-3xl font-semibold tabular text-content leading-none" data-testid="whatif-score">
                  {score?.score_before === null || score?.score_before === undefined
                    ? "n/d"
                    : num(score.score_before, 0)}
                  <ArrowRight className="inline w-5 h-5 mx-2 text-content-muted" aria-hidden="true" />
                  <span className="sr-only"> diventa </span>
                  {score?.score_after === null || score?.score_after === undefined
                    ? "n/d"
                    : num(score.score_after, 0)}
                </p>
              </div>
              <div>
                <p className="text-label text-content-muted">Differenza</p>
                <p
                  className="text-2xl font-semibold tabular text-content leading-none"
                  data-testid="whatif-score-delta"
                >
                  {score?.comparable ? signed(score.delta, 1) : "non confrontabile"}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary" data-testid="whatif-scenario">
                  {scenario?.mode === "replace"
                    ? `${scenario?.replaced_label || scenario?.replaced_key} → ${scenario?.candidate_label}`
                    : `+ ${scenario?.candidate_label} al ${pct(scenario?.candidate_weight_pct, 1)}`}
                </Badge>
                <Badge variant="secondary" data-testid="whatif-calibration">
                  Calibrazione {score?.calibration_after}
                </Badge>
                {(data.flags || []).map((flag) => (
                  <Badge key={flag} variant="warning" data-testid={`whatif-flag-${flag}`}>
                    {flag === "score_delta_not_available"
                      ? "delta non pubblicabile"
                      : flag === "candidate_coverage_limited"
                        ? "dati del candidato limitati"
                        : flag === "mixed_vintage"
                          ? "dati di date diverse"
                          : flag}
                  </Badge>
                ))}
              </div>
            </div>
            <p className="text-label text-content-muted" data-testid="whatif-score-reason">
              {score?.reason}
            </p>

            <div className="grid gap-4 lg:grid-cols-2">
              <div>
                <h3 className="text-sm font-semibold text-content mb-2">Famiglie del punteggio</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="whatif-families">
                    <thead className="text-label text-content-muted">
                      <tr className="border-b border-border">
                        <th scope="col" className="px-3 py-2 text-left font-medium">Famiglia</th>
                        <th scope="col" className="px-3 py-2 text-right font-medium">Prima</th>
                        <th scope="col" className="px-3 py-2 text-right font-medium">Dopo</th>
                        <th scope="col" className="px-3 py-2 text-right font-medium">Δ</th>
                        <th scope="col" className="px-3 py-2 text-left font-medium">Nota</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.families || []).map((family) => (
                        <tr
                          key={family.family}
                          className="border-b border-border/60 last:border-0"
                          data-testid={`whatif-family-${family.family}`}
                        >
                          <td className="px-3 py-2 text-content">{family.label}</td>
                          <td className="px-3 py-2 text-right tabular text-content-secondary">
                            {family.subscore_before === null ? "N/A" : num(family.subscore_before, 0)}
                          </td>
                          <td className="px-3 py-2 text-right tabular text-content-secondary">
                            {family.subscore_after === null ? "N/A" : num(family.subscore_after, 0)}
                          </td>
                          <td className="px-3 py-2 text-right tabular text-content">
                            {family.delta === null || family.delta === undefined
                              ? "n/d"
                              : signed(family.delta, 1)}
                          </td>
                          <td className="px-3 py-2 text-label text-content-muted">{family.note || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-content mb-2">
                  Concentrazione e duplicazione su {scenario?.dimension_label?.toLowerCase()}
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="whatif-metrics">
                    <thead className="text-label text-content-muted">
                      <tr className="border-b border-border">
                        <th scope="col" className="px-3 py-2 text-left font-medium">Metrica</th>
                        <th scope="col" className="px-3 py-2 text-right font-medium">Prima</th>
                        <th scope="col" className="px-3 py-2 text-right font-medium">Dopo</th>
                        <th scope="col" className="px-3 py-2 text-right font-medium">Δ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...(data.concentration || []), ...(data.duplication || [])].map((metric) => (
                        <tr
                          key={metric.metric}
                          className="border-b border-border/60 last:border-0"
                          data-testid={`whatif-metric-${metric.metric}`}
                        >
                          <td className="px-3 py-2 text-content">{metric.label}</td>
                          <td className="px-3 py-2 text-right tabular text-content-secondary">
                            {metric.unit === "pct_points" ? pct(metric.before, 1) : num(metric.before, 2)}
                          </td>
                          <td className="px-3 py-2 text-right tabular text-content-secondary">
                            {metric.unit === "pct_points" ? pct(metric.after, 1) : num(metric.after, 2)}
                          </td>
                          <td className="px-3 py-2 text-right tabular text-content">
                            {signed(metric.delta, 2)}
                            {metric.unit === "pct_points" && metric.delta !== null ? " pp" : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-content mb-2">
                Quanto il candidato si sovrappone a ciò che hai già
              </h3>
              <div className="flex flex-wrap gap-2" data-testid="whatif-candidate-overlap">
                {(data.candidate_overlap || []).map((entry) => (
                  <Badge
                    key={entry.versus}
                    variant={entry.versus === "portfolio" ? "secondary" : "outline"}
                    data-testid={`whatif-overlap-${entry.versus}`}
                  >
                    {entry.label}: {entry.supported ? pct(entry.classified_overlap_pct, 1) : "n/d"}
                  </Badge>
                ))}
              </div>
              <p className="text-label text-content-muted mt-2" data-testid="whatif-candidate-coverage">
                Il candidato classifica il {pct(data.candidate_coverage_pct, 1)} della propria massa su{" "}
                {scenario?.dimension_label?.toLowerCase()}. Sovrapposizione strutturale, non correlazione dei
                rendimenti: 0% non significa assenza di rischio comune.
              </p>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-content mb-2">
                Esposizioni che cambiano ({scenario?.dimension_label?.toLowerCase()})
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="whatif-exposures">
                  <thead className="text-label text-content-muted">
                    <tr className="border-b border-border">
                      <th scope="col" className="px-3 py-2 text-left font-medium">Esposizione</th>
                      <th scope="col" className="px-3 py-2 text-left font-medium">Effetto</th>
                      <th scope="col" className="px-3 py-2 text-right font-medium">Prima</th>
                      <th scope="col" className="px-3 py-2 text-right font-medium">Dopo</th>
                      <th scope="col" className="px-3 py-2 text-right font-medium">Δ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(allExposures
                      ? (data.exposure_changes || [])
                      : (data.exposure_changes || []).slice(0, 15)
                    ).map((change) => (
                      <tr
                        key={change.key}
                        className="border-b border-border/60 last:border-0"
                        data-testid={`whatif-exposure-${change.key}`}
                      >
                        <td className="px-3 py-2 text-content">{change.label}</td>
                        <td className="px-3 py-2">
                          <Badge variant={CHANGE_LABEL[change.change]?.variant || "secondary"}>
                            {CHANGE_LABEL[change.change]?.label || change.change}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-right tabular text-content-secondary">
                          {pct(change.before_pct, 2)}
                        </td>
                        <td className="px-3 py-2 text-right tabular text-content-secondary">
                          {pct(change.after_pct, 2)}
                        </td>
                        <td className="px-3 py-2 text-right tabular text-content">
                          {signed(change.delta_pct, 2)} pp
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {(data.exposure_changes || []).length > 15 && (
                <Button
                  variant="ghost"
                  className="h-10 mt-2 px-3"
                  onClick={() => setAllExposures((value) => !value)}
                  data-testid="whatif-exposures-toggle"
                >
                  {allExposures
                    ? "Mostra solo le 15 variazioni principali"
                    : `Mostra tutte le ${(data.exposure_changes || []).length} variazioni`}
                </Button>
              )}
              {data.exposure_changes_truncated && (
                <p className="text-label text-content-muted mt-2" data-testid="whatif-exposures-truncated">
                  Mostrate {data.exposure_changes_truncated.shown} variazioni su{" "}
                  {data.exposure_changes_truncated.total}; le altre valgono{" "}
                  {pct(data.exposure_changes_truncated.hidden_contribution_pct, 2)} in totale.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <p className="text-label text-content-muted" data-testid="whatif-provenance">
                {data.provenance?.note}
              </p>
              {(data.warnings || []).map((warning) => (
                <p
                  key={warning}
                  className="flex items-start gap-2 text-label text-content-muted"
                  data-testid="whatif-warning"
                >
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                  <span>{warning}</span>
                </p>
              ))}
            </div>
          </div>
        )}
      </div>
    </SectionCard>
  );
}

export default WhatIfPanel;
